/**
 * PCO Groups API Types
 * API Base: https://api.planningcenteronline.com/groups/v2
 */

// ============================================
// Group Types
// ============================================

export interface PcoGroupType {
  id: string;
  type: 'GroupType';
  attributes: {
    church_center_visible: boolean;
    church_center_map_visible: boolean;
    color: string;
    default_group_settings: string | null;
    description: string | null;
    name: string;
    position: number;
    created_at: string;
    updated_at: string;
  };
  relationships?: {
    groups?: {
      data: Array<{ type: 'Group'; id: string }>;
    };
  };
}

// ============================================
// Groups
// ============================================

export interface PcoGroup {
  id: string;
  type: 'Group';
  attributes: {
    archived_at: string | null;
    contact_email: string | null;
    created_at: string;
    description: string | null;
    enrollment_open: boolean;
    enrollment_strategy: 'open_signup' | 'request_to_join' | 'closed';
    events_visibility: 'members' | 'leaders' | 'public';
    header_image: {
      thumbnail: string | null;
      medium: string | null;
      original: string | null;
    } | null;
    location_type_preference: 'physical' | 'virtual' | 'no_preference';
    memberships_count: number;
    name: string;
    public_church_center_web_url: string | null;
    schedule: string | null;
    virtual_location_url: string | null;
    widget_status: string | null;
    updated_at?: string;
  };
  relationships?: {
    group_type?: {
      data: { type: 'GroupType'; id: string } | null;
    };
    location?: {
      data: { type: 'Location'; id: string } | null;
    };
  };
}

// ============================================
// Group Memberships
// ============================================

export type GroupMembershipRole = 'member' | 'leader';

export interface PcoGroupMembership {
  id: string;
  type: 'Membership';
  attributes: {
    account_center_identifier: string | null;
    avatar_url: string | null;
    color_identifier: string | null;
    email_address: string | null;
    first_name: string;
    joined_at: string;
    last_name: string;
    phone_number: string | null;
    role: GroupMembershipRole;
  };
  relationships?: {
    group?: {
      data: { type: 'Group'; id: string };
    };
    person?: {
      data: { type: 'Person'; id: string };
    };
  };
}

// ============================================
// Group Events
// ============================================

export interface PcoGroupEvent {
  id: string;
  type: 'Event';
  attributes: {
    attendance_requests_enabled: boolean;
    automated_reminder_enabled: boolean;
    canceled: boolean;
    canceled_at: string | null;
    description: string | null;
    ends_at: string;
    location_type_preference: 'physical' | 'virtual' | 'no_preference';
    multi_day: boolean;
    name: string;
    reminders_sent: boolean;
    reminders_sent_at: string | null;
    repeating: boolean;
    starts_at: string;
    virtual_location_url: string | null;
  };
  relationships?: {
    group?: {
      data: { type: 'Group'; id: string };
    };
    location?: {
      data: { type: 'Location'; id: string } | null;
    };
  };
}

// ============================================
// Locations
// ============================================

export interface PcoGroupLocation {
  id: string;
  type: 'Location';
  attributes: {
    display_preference: 'exact' | 'approximate' | 'hidden';
    full_formatted_address: string | null;
    latitude: number | null;
    longitude: number | null;
    name: string;
    radius: number | null;
    strategy: 'exact' | 'approximate';
  };
}

/** PCO Groups API association: GET /groups/v2/groups/:id/campuses */
export interface PcoGroupCampus {
  id: string;
  type: 'Campus';
  attributes: {
    name: string;
  };
}

// ============================================
// API Response Types
// ============================================

export interface PcoGroupsApiResponse<T> {
  data: T;
  included?: Array<
    | PcoGroupType
    | PcoGroup
    | PcoGroupMembership
    | PcoGroupLocation
    | PcoGroupCampus
  >;
  meta?: {
    total_count?: number;
    count?: number;
    next?: {
      offset: number;
    };
    can_order_by?: string[];
    can_query_by?: string[];
    can_include?: string[];
    can_filter?: string[];
    parent?: {
      id: string;
      type: string;
    };
  };
  links?: {
    self: string;
    next?: string;
    prev?: string;
  };
}

// ============================================
// Local Storage Types
// ============================================

/** One group leader / assistant row: PCO person is stable; MHX user id is filled when linked. */
export interface PcoGroupLeaderRef {
  pco_person_id: string;
  mhx_user_id?: string | null;
}

function parseObjectLeaderSlots(arr: unknown): PcoGroupLeaderRef[] | null {
  if (!Array.isArray(arr)) {
    return null;
  }

  if (arr.length === 0) {
    return [];
  }

  const out: PcoGroupLeaderRef[] = [];
  for (const item of arr) {
    if (typeof item === 'string') {
      return null;
    }

    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const pco = String(rec.pco_person_id ?? '').trim();
      const rawMhx = rec.mhx_user_id;
      const mhx =
        rawMhx === null || rawMhx === undefined
          ? null
          : String(rawMhx).trim() || null;
      if (pco || mhx) {
        out.push({
          pco_person_id: pco,
          ...(mhx ? { mhx_user_id: mhx } : {}),
        });
      }
    }
  }

  return out;
}

function legacyUserIdsToSlots(arr: unknown): PcoGroupLeaderRef[] {
  if (!Array.isArray(arr)) {
    return [];
  }

  return arr
    .filter((x): x is string => typeof x === 'string' && Boolean(x.trim()))
    .map((mhx_user_id) => ({
      pco_person_id: '',
      mhx_user_id: mhx_user_id.trim(),
    }));
}

/**
 * Merge new leader slot arrays with legacy `mhx_*_user_ids` string lists from Mongo.
 */
export function normalizePcoGroupLeaderRows(doc: Record<string, unknown>): {
  mhx_group_leaders: PcoGroupLeaderRef[];
  mhx_assistant_group_leaders: PcoGroupLeaderRef[];
} {
  const glParsed = parseObjectLeaderSlots(doc.mhx_group_leaders);
  const mhx_group_leaders =
    glParsed !== null
      ? glParsed
      : legacyUserIdsToSlots(doc.mhx_group_leader_user_ids);

  const aglParsed = parseObjectLeaderSlots(doc.mhx_assistant_group_leaders);
  const mhx_assistant_group_leaders =
    aglParsed !== null
      ? aglParsed
      : legacyUserIdsToSlots(doc.mhx_assistant_group_leader_user_ids);

  return { mhx_group_leaders, mhx_assistant_group_leaders };
}

export interface GroupDocument {
  id?: string;
  _id?: unknown;
  pco_id: string;
  tenant_id: string;
  group_type_id: string | null;
  pco_group_type_id?: string | null;
  name: string;
  description: string | null;
  contact_email: string | null;
  schedule: string | null;
  enrollment_open: boolean;
  enrollment_strategy: string;
  events_visibility: string;
  location_type_preference: string;
  memberships_count: number;
  header_image_url: string | null;
  public_url: string | null;
  virtual_location_url: string | null;
  archived_at: Date | null;
  pco_created_at: Date;
  pco_updated_at: Date;
  synced_at: Date;
  created_at: Date;
  updated_at: Date;
  mhx_group_leaders?: PcoGroupLeaderRef[];
  mhx_assistant_group_leaders?: PcoGroupLeaderRef[];
  /** @deprecated Legacy overlay; use `mhx_group_leaders` / `mhx_assistant_group_leaders`. */
  mhx_group_leader_user_ids?: string[];
  mhx_assistant_group_leader_user_ids?: string[];
  mhx_parent_id?: string | null;
  mhx_campus_pco_id?: string | null;
  /** PCO People campus ids from Groups API (GET .../groups/:id/campuses), updated on sync. */
  pco_campus_ids?: string[];
}

/** Minimal PCO group fields for access control (client + server). */
export interface PcoGroupAccessShape {
  pco_id: string;
  mhx_group_leaders?: PcoGroupLeaderRef[];
  mhx_assistant_group_leaders?: PcoGroupLeaderRef[];
  /** @deprecated */
  mhx_group_leader_user_ids?: string[];
  mhx_assistant_group_leader_user_ids?: string[];
  mhx_parent_id?: string | null;
  mhx_campus_pco_id?: string | null;
  /** From PCO sync; used for campus-scoped PCO group leader roles when mhx_campus_pco_id is unset. */
  pco_campus_ids?: string[];
}

/** Firestore user document ids with PCO group leader / assistant leader overlay access. */
export function pcoGroupLeaderMhxUserIds(g: PcoGroupAccessShape): string[] {
  const hasNewGl = g.mhx_group_leaders !== undefined;
  const hasNewAgl = g.mhx_assistant_group_leaders !== undefined;
  if (hasNewGl || hasNewAgl) {
    const fromSlots = (slots?: PcoGroupLeaderRef[]) =>
      (slots ?? []).map((s) => (s.mhx_user_id ?? '').trim()).filter(Boolean);

    return [
      ...new Set([
        ...fromSlots(g.mhx_group_leaders),
        ...fromSlots(g.mhx_assistant_group_leaders),
      ]),
    ];
  }

  return [
    ...new Set([
      ...(g.mhx_group_leader_user_ids ?? []),
      ...(g.mhx_assistant_group_leader_user_ids ?? []),
    ]),
  ];
}

/**
 * Campus-scoped PCO group leader access: optional manual campus (mhx) overrides synced PCO campuses.
 */
export function pcoGroupMatchesCampusAccess(
  g: PcoGroupAccessShape,
  campusId: string,
): boolean {
  const manual = g.mhx_campus_pco_id;
  if (manual !== undefined && manual !== null && String(manual).trim() !== '') {
    return String(manual) === campusId;
  }

  return (g.pco_campus_ids ?? []).includes(campusId);
}

export interface GroupTypeDocument {
  id?: string;
  _id?: unknown;
  pco_id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  color: string;
  position: number;
  church_center_visible: boolean;
  church_center_map_visible: boolean;
  pco_created_at: Date;
  pco_updated_at: Date;
  synced_at: Date;
  created_at: Date;
  updated_at: Date;
}

export interface GroupMembershipDocument {
  id?: string;
  pco_id: string;
  tenant_id: string;
  group_id: string; // Local group ID
  pco_group_id: string;
  pco_person_id: string | null;
  person_id: string | null; // Local person ID (if synced)
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: GroupMembershipRole;
  joined_at: Date;
  synced_at: Date;
  created_at: Date;
  updated_at: Date;
}

// ============================================
// Sync Types
// ============================================

export interface GroupsSyncResult {
  success: boolean;
  groupTypes: {
    total: number;
    synced: number;
  };
  groups: {
    total: number;
    synced: number;
  };
  memberships: {
    total: number;
    synced: number;
  };
  errors: string[];
  duration: number;
}

export interface GroupWithMemberships {
  group: PcoGroup;
  groupType?: PcoGroupType;
  memberships: PcoGroupMembership[];
  campuses?: PcoGroupCampus[];
}
